import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import type {
  ChatUserPreference,
  ChatUserRecentEntity,
  ChatUserFact,
} from '../../services/api/chatUserMemoryService';
import {
  addAccountThunk,
  addAccountWithUserData,
  completeAccountSwitchWithUserData,
  processAddAccountThunk,
  switchAccountThunk,
} from '../thunks/multiAccountThunks';

/**
 * Spec C (chat-user-memory), Phase 9 — Redux state for the chat user memory
 * inspector. Holds three lists (preferences, recent entities, facts), the
 * panel open/closed flag, the active tab, and standard loading/error state.
 *
 * Mirrors the Spec B `chatSessionSlice` reset-on-identity-change pattern so
 * the cached memory wipes when the user switches account or active tenant —
 * each (user, tenant?) bucket is a distinct memory scope per Requirement 11.3.
 */
export type ChatUserMemoryTab = 'preferences' | 'recentEntities' | 'facts';

interface ChatUserMemoryState {
  preferences: ChatUserPreference[];
  recentEntities: ChatUserRecentEntity[];
  facts: ChatUserFact[];
  isPanelOpen: boolean;
  activeTab: ChatUserMemoryTab;
  isLoading: boolean;
  error: string | null;
}

const initialState: ChatUserMemoryState = {
  preferences: [],
  recentEntities: [],
  facts: [],
  isPanelOpen: false,
  activeTab: 'preferences',
  isLoading: false,
  error: null,
};

function applyReset(state: ChatUserMemoryState) {
  state.preferences = [];
  state.recentEntities = [];
  state.facts = [];
  state.isPanelOpen = false;
  state.activeTab = 'preferences';
  state.isLoading = false;
  state.error = null;
}

const chatUserMemorySlice = createSlice({
  name: 'chatUserMemory',
  initialState,
  reducers: {
    setPanelOpen(state, action: PayloadAction<boolean>) {
      state.isPanelOpen = action.payload;
    },
    togglePanel(state) {
      state.isPanelOpen = !state.isPanelOpen;
    },
    setActiveTab(state, action: PayloadAction<ChatUserMemoryTab>) {
      state.activeTab = action.payload;
    },

    // -------- Preferences --------
    setPreferences(state, action: PayloadAction<ChatUserPreference[]>) {
      state.preferences = action.payload;
      state.isLoading = false;
      state.error = null;
    },
    upsertPreferenceLocal(state, action: PayloadAction<ChatUserPreference>) {
      const incoming = action.payload;
      const idx = state.preferences.findIndex(p => p.prefKey === incoming.prefKey);
      if (idx >= 0) {
        state.preferences[idx] = incoming;
      } else {
        state.preferences.unshift(incoming);
      }
    },
    removePreferenceLocal(state, action: PayloadAction<string>) {
      state.preferences = state.preferences.filter(p => p.prefKey !== action.payload);
    },

    // -------- Recent entities --------
    setRecentEntities(state, action: PayloadAction<ChatUserRecentEntity[]>) {
      state.recentEntities = action.payload;
      state.isLoading = false;
      state.error = null;
    },
    removeRecentEntityLocal(state, action: PayloadAction<number>) {
      state.recentEntities = state.recentEntities.filter(e => e.id !== action.payload);
    },
    clearAllRecentEntitiesLocal(state) {
      state.recentEntities = [];
    },

    // -------- Facts --------
    setFacts(state, action: PayloadAction<ChatUserFact[]>) {
      state.facts = action.payload;
      state.isLoading = false;
      state.error = null;
    },
    upsertFactLocal(state, action: PayloadAction<ChatUserFact>) {
      const incoming = action.payload;
      const idx = state.facts.findIndex(f => f.id === incoming.id);
      if (idx >= 0) {
        state.facts[idx] = incoming;
      } else {
        state.facts.unshift(incoming);
      }
    },
    removeFactLocal(state, action: PayloadAction<number>) {
      state.facts = state.facts.filter(f => f.id !== action.payload);
    },

    // -------- Loading / error --------
    setLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
      state.isLoading = false;
    },

    /**
     * Full reset for an account-identity change. Mirrors the Spec B
     * chatSessionSlice pattern — see AuthProvider's effect that watches
     * activeAccountId.
     */
    resetForAccountChange(state) {
      applyReset(state);
    },
    /**
     * Reset when the user switches active tenant within the same account.
     * Each (user, tenant?) bucket is a distinct memory scope — wipe the
     * cached lists and let the inspector refetch on next open.
     */
    resetForTenantChange(state) {
      applyReset(state);
    },
  },
  extraReducers: (builder) => {
    // Defensive matcher: if any of these account thunks fire, also reset.
    builder.addMatcher(
      isAnyOf(
        switchAccountThunk.fulfilled,
        addAccountThunk.fulfilled,
        addAccountWithUserData.fulfilled,
        processAddAccountThunk.fulfilled,
        completeAccountSwitchWithUserData.fulfilled,
      ),
      (state) => {
        applyReset(state);
      }
    );
  },
});

export const {
  setPanelOpen,
  togglePanel,
  setActiveTab,
  setPreferences,
  upsertPreferenceLocal,
  removePreferenceLocal,
  setRecentEntities,
  removeRecentEntityLocal,
  clearAllRecentEntitiesLocal,
  setFacts,
  upsertFactLocal,
  removeFactLocal,
  setLoading,
  setError,
  resetForAccountChange,
  resetForTenantChange,
} = chatUserMemorySlice.actions;

export default chatUserMemorySlice.reducer;
